// @ts-nocheck

/**
 * useGroupNodeAdoption — Hook that auto-parents nodes when they are dragged
 * over a group node.
 *
 * How it works:
 *  1. On `onNodeDragStop`, check if the dragged node's absolute position
 *     overlaps any group node's bounding box.
 *  2. If it does and the node is not already a child → adopt it
 *     (set parentId, compute relative position).
 *  3. Children can ONLY be detached via the explicit `detachNodeFromGroup`
 *     utility (triggered by the Detach button on hover).
 *
 * The hook returns a stable `handleNodeDragStop` callback that should be
 * passed to ReactFlow's `onNodeDragStop` prop.
 *
 * Usage inside SolyticsFlow:
 *   const handleNodeDragStop = useGroupNodeAdoption(nodes, setNodes, onNodeAdopted, onNodeOrphaned);
 *   <ReactFlow … onNodeDragStop={handleNodeDragStop} />
 *
 * For sidebar DnD drops, use the exported `adoptNodeOnDrop` utility:
 *   const adoptedNode = adoptNodeOnDrop(dropPosition, newNode, existingNodes);
 *
 * For explicit detach (Detach button):
 *   detachNodeFromGroup(nodeId, setNodes, nodes, onNodeOrphaned);
 */
import { useCallback } from 'react';

const DEFAULT_CHILD_WIDTH = 200;
const DEFAULT_CHILD_HEIGHT = 36;
const DEFAULT_GROUP_WIDTH = 200;
const DEFAULT_GROUP_HEIGHT = 250;

function normalizeDimension(value, fallback) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;

  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return parsed;
  }

  return fallback;
}

function getNodeWidth(node, fallback = DEFAULT_CHILD_WIDTH) {
  return normalizeDimension(
    node.measured?.width ?? node.width ?? node.style?.width,
    fallback
  );
}

function getNodeHeight(node, fallback = DEFAULT_CHILD_HEIGHT) {
  return normalizeDimension(
    node.measured?.height ?? node.height ?? node.style?.height,
    fallback
  );
}

function getAbsolutePosition(node, nodeMap) {
  if (!node?.parentId) {
    return node?.position ?? { x: 0, y: 0 };
  }

  const parent = nodeMap.get(node.parentId);
  if (!parent) return node.position ?? { x: 0, y: 0 };

  return {
    x: (parent.position?.x ?? 0) + (node.position?.x ?? 0),
    y: (parent.position?.y ?? 0) + (node.position?.y ?? 0),
  };
}

/**
 * Check if point (px, py) is inside a rectangle.
 */
function isInsideRect(px, py, rect) {
  return (
    px >= rect.x &&
    px <= rect.x + rect.width &&
    py >= rect.y &&
    py <= rect.y + rect.height
  );
}

/**
 * Find the best (smallest) group node whose bounding box contains
 * the given absolute point (centerX, centerY).
 *
 * @param {number} centerX
 * @param {number} centerY
 * @param {Array}  groupNodes — pre-filtered list of group nodes
 * @returns {Object|null} the best-fit group node, or null
 */
function findBestGroup(centerX, centerY, groupNodes) {
  let bestGroup = null;
  let bestArea = Infinity;

  for (const group of groupNodes) {
    const gw = getNodeWidth(group, DEFAULT_GROUP_WIDTH);
    const gh = getNodeHeight(group, DEFAULT_GROUP_HEIGHT);
    const rect = {
      x: group.position.x,
      y: group.position.y,
      width: gw,
      height: gh,
    };

    if (isInsideRect(centerX, centerY, rect)) {
      const area = gw * gh;
      if (area < bestArea) {
        bestArea = area;
        bestGroup = group;
      }
    }
  }

  return bestGroup;
}

/**
 * adoptNodeOnDrop — Standalone utility to check if a freshly dropped node
 * (from sidebar DnD) landed inside a group and, if so, return it with the
 * correct parentId and relative position.
 *
 * Call this from your `onDrop` handler before inserting the node into state.
 *
 * @param {{ x: number, y: number }} dropPosition — absolute flow-space position
 * @param {Object} newNode   — the node object about to be inserted
 * @param {Array}  allNodes  — the current nodes array (to find groups)
 * @returns {Object} the node, possibly with parentId and adjusted position
 */
export function adoptNodeOnDrop(dropPosition, newNode, allNodes) {
  // Don't try to parent group nodes inside other groups
  if (newNode.type === 'group') return newNode;

  const groupNodes = allNodes.filter(
    (n) => n.type === 'group' && n.id !== newNode.id
  );

  if (groupNodes.length === 0) return newNode;

  const nodeWidth = getNodeWidth(newNode);
  const nodeHeight = getNodeHeight(newNode);

  const centerX = dropPosition.x + nodeWidth / 2;
  const centerY = dropPosition.y + nodeHeight / 2;

  const bestGroup = findBestGroup(centerX, centerY, groupNodes);

  if (bestGroup) {
    const relX = dropPosition.x - bestGroup.position.x;
    const relY = dropPosition.y - bestGroup.position.y;
    return {
      ...newNode,
      parentId: bestGroup.id,
      extent: 'parent',
      position: { x: relX, y: relY },
    };
  }

  return newNode;
}

/**
 * Ensure nodes with parentId appear after their parent in the array.
 * ReactFlow v12 requires children to come after parents.
 *
 * @param {Array} nodes
 * @returns {Array} re-ordered nodes
 */
export function ensureChildAfterParent(nodes) {
  const parentIds = new Set(
    nodes.filter((n) => n.type === 'group').map((n) => n.id)
  );
  const parents = [];
  const children = [];
  const others = [];

  for (const node of nodes) {
    if (parentIds.has(node.id)) {
      parents.push(node);
    } else if (node.parentId && parentIds.has(node.parentId)) {
      children.push(node);
    } else {
      others.push(node);
    }
  }

  // Groups first, then non-parented nodes, then children
  return [...parents, ...others, ...children];
}

/**
 * detachNodeFromGroup — Explicit utility to remove a child node from its
 * parent group. Converts position from relative back to absolute.
 *
 * Called by the Detach button on hover.
 *
 * @param {string}   nodeId         — ID of the child node to detach
 * @param {Function} setNodes       — React state setter
 * @param {Array}    nodes          — current nodes array
 * @param {Function} onNodeOrphaned — optional callback (nodeId, oldParentId)
 */
export function detachNodeFromGroup(nodeId, setNodes, nodes, onNodeOrphaned) {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node || !node.parentId) return;

  const parent = nodes.find((n) => n.id === node.parentId);
  const oldParentId = node.parentId;

  // Convert relative position to absolute
  const absX = node.position.x + (parent?.position?.x ?? 0);
  const absY = node.position.y + (parent?.position?.y ?? 0);

  setNodes((prev) => {
    const updated = prev.map((n) => {
      if (n.id !== nodeId) return n;
      return {
        ...n,
        parentId: undefined,
        extent: undefined,
        position: { x: absX, y: absY },
      };
    });
    return ensureChildAfterParent(updated);
  });

  onNodeOrphaned?.(nodeId, oldParentId);
}

/**
 * clampChildPosition — Clamp a child node's relative position so it
 * stays within its parent group's bounds.
 *
 * @param {Object} childNode — the child node being dragged
 * @param {Object} parentNode — the parent group node
 * @returns {{ x: number, y: number }} clamped position
 */
export function clampChildPosition(childNode, parentNode) {
  const pw = getNodeWidth(parentNode, DEFAULT_GROUP_WIDTH);
  const ph = getNodeHeight(parentNode, DEFAULT_GROUP_HEIGHT);
  const cw = getNodeWidth(childNode);
  const ch = getNodeHeight(childNode);

  // Leave some padding inside the group
  const pad = 5;
  const minX = pad;
  const minY = pad;
  const maxX = Math.max(minX, pw - cw - pad);
  const maxY = Math.max(minY, ph - ch - pad);

  return {
    x: Math.max(minX, Math.min(childNode.position.x, maxX)),
    y: Math.max(minY, Math.min(childNode.position.y, maxY)),
  };
}

export default function useGroupNodeAdoption(nodes, setNodes, onNodeAdopted) {
  return useCallback(
    (_event, draggedNode) => {
      // Don't try to parent group nodes inside other groups
      if (draggedNode.type === 'group') return;

      const groupNodes = nodes.filter(
        (n) => n.type === 'group' && n.id !== draggedNode.id
      );

      if (draggedNode.parentId) {
        setNodes((prev) => {
          const parentNode = prev.find((n) => n.id === draggedNode.parentId);
          if (!parentNode) return prev;

          const updated = prev.map((n) => {
            if (n.id !== draggedNode.id) return n;

            const clampedPosition = clampChildPosition(n, parentNode);
            if (
              clampedPosition.x === n.position.x &&
              clampedPosition.y === n.position.y &&
              n.extent === 'parent'
            ) {
              return n;
            }

            return {
              ...n,
              extent: 'parent',
              position: clampedPosition,
            };
          });

          return ensureChildAfterParent(updated);
        });

        return;
      }

      if (groupNodes.length === 0) return;

      const nodeMap = new Map(nodes.map((node) => [node.id, node]));

      // Compute the dragged node's absolute center
      const nodeWidth = getNodeWidth(draggedNode);
      const nodeHeight = getNodeHeight(draggedNode);

      const absolutePosition = getAbsolutePosition(draggedNode, nodeMap);
      const absX = absolutePosition.x;
      const absY = absolutePosition.y;

      const centerX = absX + nodeWidth / 2;
      const centerY = absY + nodeHeight / 2;

      const bestGroup = findBestGroup(centerX, centerY, groupNodes);

      if (!bestGroup) return;

      setNodes((prev) => {
        const updated = prev.map((n) => {
          if (n.id !== draggedNode.id) return n;

          // Node should be adopted by a group
          if (n.parentId !== bestGroup.id) {
            const relX = absX - bestGroup.position.x;
            const relY = absY - bestGroup.position.y;
            onNodeAdopted?.(n.id, bestGroup.id);
            return {
              ...n,
              parentId: bestGroup.id,
              extent: 'parent',
              position: { x: relX, y: relY },
            };
          }

          return n;
        });

        return ensureChildAfterParent(updated);
      });
    },
    [nodes, setNodes, onNodeAdopted]
  );
}
